import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination } from './ui';
import {
  useC360List, C360Table, ColumnChooser, ViewIcon, buildSelectFields,
} from './c360';
import { usePageActions } from '../contexts/PermissionContext';
import { AllocationModal } from './finance/AllocationModal';
import {
  FT_API, CUSTOMER_API, REPRESENTATIVE_API,
  DIRECTION_COLLECTION, STATUS_ACTIVE, METHOD_LABELS, transactionDetailRoute,
  fetchTransaction, postAllocate, normalizeAllocation,
  fmtMoney, fmtDate, pick, unwrap, toNum, resolveNames,
} from './finance/allocationShared';

const muted = 'var(--text-secondary, #6b7280)';
const strong = 'var(--card-text-strong, #0b2046)';
const border = '1px solid var(--border-color, rgba(15,23,42,0.10))';

const st = {
  summary: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12,
    padding: '12px 14px', marginBottom: 12, border, borderRadius: 10, background: 'var(--bg-card, #fff)',
  },
  sumLabel: { color: muted, fontSize: 11, fontWeight: 800, textTransform: 'uppercase' },
  sumValue: { color: strong, fontSize: 18, fontWeight: 800 },
  hint: { color: muted, fontSize: 12, marginTop: 2 },
  offlineChip: {
    fontSize: 11, fontWeight: 700, padding: '2px 6px', borderRadius: 5,
    background: 'rgba(253,171,61,0.14)', color: 'var(--warn, #fdab3d)',
  },
};

/**
 * DAĞITILMAMIŞ TAHSİLATLAR (#Faz2, spec §6-§7) — menü 9737, /finance/unallocated.
 *
 * Faturaya bağlanamamış (avans / çevrimdışı) tahsilat kuyruğu. Eskiden bu para "mobile-advance"
 * PLACEHOLDER FATURA uydurularak gizleniyordu; artık görünür bir iş kuyruğuna düşer.
 *
 * Liste OData ile beslenir (Direction=Tahsilat, Status=Aktif, UnallocatedAmount > 0) — c360 liste
 * altyapısı sayfalama/sıralama/kolon filtresi verir ve OData Get'i #131 veri yetkisini uygular
 * (dedicated /unallocated ucuyla aynı kapsam). Özet şeridi /unallocated ucundan gelir.
 */
export function UnallocatedCollectionsPage() {
  const navigate = useNavigate();
  const { canEdit } = usePageActions();

  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [custMap, setCustMap] = useState({});
  const [repMap, setRepMap] = useState({});
  const [summary, setSummary] = useState({ totalUnallocated: 0, count: 0 });

  // Eşleştirme modalı
  const [modal, setModal] = useState(null); // { transaction, existingAllocations }
  const [advanceConfirm, setAdvanceConfirm] = useState(null); // { transaction, existingAllocations }
  const [busy, setBusy] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const COLUMN_FILTER_META = useMemo(() => ({
    documentNo: { type: 'text', odataField: 'DocumentNo' },
  }), []);
  const searchFields = useMemo(() => ['DocumentNo', 'Description'], []);

  const selectFields = useMemo(
    () => buildSelectFields({
      columnFilterMeta: COLUMN_FILTER_META,
      columnVisibility,
      always: [
        'Id', 'CustomerId', 'RepresentativeId', 'Amount', 'AllocatedAmount', 'UnallocatedAmount',
        'TransactionDate', 'Method', 'WasOffline', 'DocumentNo', 'Status', 'Direction',
      ],
    }),
    [columnVisibility, COLUMN_FILTER_META],
  );

  // Kuyruk: yalnız aktif tahsilatların DAĞITILMAMIŞ bakiyesi olanları.
  const baseFilter = `Direction eq ${DIRECTION_COLLECTION} and Status eq ${STATUS_ACTIVE} and UnallocatedAmount gt 0`;

  const list = useC360List({
    apiBase: FT_API,
    baseFilter,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields,
    defaultSortField: 'TransactionDate',
    defaultSortDir: 'asc', // en eski bekleyen para en üstte (kuyruk mantığı)
    selectFields,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData,
  } = list;

  // Özet şeridi — /unallocated ucu (toplam bekleyen para + kayıt sayısı).
  const loadSummary = useCallback(async () => {
    try {
      const res = await fetch(`${FT_API}/unallocated`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = unwrap(await res.json());
      setSummary({
        totalUnallocated: toNum(pick(data, 'totalUnallocated', 'TotalUnallocated')),
        count: toNum(pick(data, 'count', 'Count')),
      });
    } catch { /* özet kritik değil — sessiz geç */ }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const refreshAll = useCallback(() => { fetchData(); loadSummary(); }, [fetchData, loadSummary]);

  // Görünen satırların müşteri / temsilci adlarını topluca çöz.
  useEffect(() => {
    const ids = rows.map((r) => r.customerId).filter((id) => id && !custMap[id]);
    if (ids.length === 0) return;
    resolveNames(CUSTOMER_API, ids, 'Title').then((m) => {
      if (Object.keys(m).length) setCustMap((prev) => ({ ...prev, ...m }));
    });
  }, [rows, custMap]);

  useEffect(() => {
    const ids = rows.map((r) => r.representativeId).filter((id) => id && !repMap[id]);
    if (ids.length === 0) return;
    resolveNames(REPRESENTATIVE_API, ids, 'Name').then((m) => {
      if (Object.keys(m).length) setRepMap((prev) => ({ ...prev, ...m }));
    });
  }, [rows, repMap]);

  // Tahsilatın mevcut eşleştirmelerini de getir (tavan düzeltmesi + ön-doldurma için).
  const openWith = useCallback(async (row, setter) => {
    setBusy(true);
    try {
      const tx = await fetchTransaction(row.id);
      const allocations = (pick(tx, 'collectionAllocations', 'CollectionAllocations') || [])
        .map(normalizeAllocation);
      setter({
        transaction: {
          id: row.id,
          customerId: row.customerId,
          amount: toNum(row.amount),
          documentNo: row.documentNo,
          transactionDate: row.transactionDate,
        },
        existingAllocations: allocations,
      });
    } catch (e) {
      showToast('error', e.message || 'Tahsilat alınamadı');
    } finally {
      setBusy(false);
    }
  }, []);

  // "Avans Bırak" — tahsilatın TÜM fatura eşleştirmesini kaldırır, tamamı avansa döner.
  // Zaten tamamı avanssa yazma yapılmaz (gereksiz denetim izi bırakmamak için).
  const leaveAsAdvance = async () => {
    const ctx = advanceConfirm;
    if (!ctx) return;
    if (ctx.existingAllocations.length === 0) {
      setAdvanceConfirm(null);
      showToast('success', 'Tahsilat zaten tamamen avans olarak duruyor.');
      return;
    }
    setBusy(true);
    try {
      await postAllocate(ctx.transaction.id, []);
      showToast('success', 'Eşleştirme kaldırıldı — tahsilatın tamamı avans olarak bırakıldı.');
      setAdvanceConfirm(null);
      refreshAll();
    } catch (e) {
      showToast('error', e.message || 'İşlem başarısız');
    } finally {
      setBusy(false);
    }
  };

  const openDetail = (row) => {
    // Rota yön+yöntemden çözülür (financeTypeMeta). Kuyruk yalnız DIRECTION_COLLECTION içerir;
    // yine de kaydın kendi yönü geçilir — filtre gevşerse yanlış gruba götürmesin.
    const route = transactionDetailRoute(toNum(row.direction) || DIRECTION_COLLECTION, toNum(row.method), row.id);
    if (route) navigate(route, { state: { row } });
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('documentNo', {
      header: 'Tahsilat No', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('transactionDate', {
      header: 'Tarih', enableColumnFilter: false,
      cell: (info) => fmtDate(info.getValue()),
    }),
    columnHelper.display({
      id: 'customer', header: 'Müşteri', enableHiding: false,
      cell: ({ row }) => (row.original.customerId ? (custMap[row.original.customerId] || '…') : '-'),
    }),
    columnHelper.display({
      id: 'representative', header: 'Plasiyer',
      cell: ({ row }) => (row.original.representativeId ? (repMap[row.original.representativeId] || '…') : '-'),
    }),
    columnHelper.accessor('amount', {
      header: 'Tutar', enableColumnFilter: false,
      cell: (info) => fmtMoney(info.getValue()),
    }),
    columnHelper.accessor('unallocatedAmount', {
      header: 'Dağıtılmamış', enableColumnFilter: false,
      cell: (info) => (
        <b style={{ color: 'var(--warn, #fdab3d)', fontWeight: 800 }}>{fmtMoney(info.getValue())}</b>
      ),
    }),
    columnHelper.accessor('method', {
      header: 'Yöntem', enableColumnFilter: false,
      cell: (info) => METHOD_LABELS[toNum(info.getValue())] || '-',
    }),
    columnHelper.accessor('wasOffline', {
      header: 'Offline mi', enableColumnFilter: false,
      cell: (info) => (info.getValue()
        ? <span style={st.offlineChip}>Çevrimdışı</span>
        : <span style={{ color: muted }}>Online</span>),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => {
        const r = row.original;
        return (
          <span className="c360-actions">
            <button
              type="button"
              className="c360-action-btn c360-action-btn--icon c360-action-btn--view"
              title="Tahsilatı Görüntüle"
              onClick={(e) => { e.stopPropagation(); openDetail(r); }}
            >
              <ViewIcon />
            </button>
            {canEdit && (
              <>
                <button
                  type="button"
                  className="c360-action-btn"
                  title="Açık faturalara dağıt"
                  disabled={busy}
                  onClick={(e) => { e.stopPropagation(); setSelected(r); openWith(r, setModal); }}
                >
                  Faturaya Eşle
                </button>
                <button
                  type="button"
                  className="c360-action-btn"
                  title="Faturaya eşleme, avans olarak bırak"
                  disabled={busy}
                  onClick={(e) => { e.stopPropagation(); setSelected(r); openWith(r, setAdvanceConfirm); }}
                >
                  Avans Bırak
                </button>
              </>
            )}
          </span>
        );
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [custMap, repMap, canEdit, busy, openWith]);

  const sorting = useMemo(
    () => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []),
    [sortField, sortDir],
  );
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('TransactionDate', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Tahsilat no veya açıklama ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={refreshAll}>Yenile</button>
      <ColumnChooser table={table} />
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Dağıtılmamış Tahsilatlar"
        subtitle="Faturaya bağlanmamış (avans / çevrimdışı) tahsilat kuyruğu — açık faturalara eşleyin."
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <div style={st.summary}>
          <div>
            <div style={st.sumLabel}>Bekleyen Toplam</div>
            <div style={{ ...st.sumValue, color: 'var(--warn, #fdab3d)' }}>{fmtMoney(summary.totalUnallocated)}</div>
            <div style={st.hint}>Faturaya eşleşmeyi bekleyen para</div>
          </div>
          <div>
            <div style={st.sumLabel}>Tahsilat Sayısı</div>
            <div style={st.sumValue}>{summary.count}</div>
            <div style={st.hint}>Kuyruktaki kayıt</div>
          </div>
          <div>
            <div style={st.sumLabel}>Çevrimdışı Kayıt</div>
            <div style={st.sumValue}>{rows.filter((r) => r.wasOffline).length}</div>
            <div style={st.hint}>Bu sayfadaki, cihazda çevrimdışı alınan tahsilatlar</div>
          </div>
        </div>

        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Dağıtılmamış tahsilat yok — tüm para faturalara eşleşmiş."
        />
      </ListPageShell>

      <AllocationModal
        open={!!modal}
        mode="allocate"
        transaction={modal?.transaction}
        existingAllocations={modal?.existingAllocations || []}
        onClose={() => setModal(null)}
        onError={(msg) => showToast('error', msg)}
        onSaved={(_r, info) => {
          setModal(null);
          showToast('success', `Eşleştirme kaydedildi — Dağıtılan ${fmtMoney(info.allocated)} · Avans ${fmtMoney(info.unallocated)}`);
          refreshAll();
        }}
      />

      {advanceConfirm && (
        <div style={s.overlay} onClick={() => setAdvanceConfirm(null)}>
          <div style={{ ...s.modal, maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={s.modalTitle}>Avans Bırak</h3></div>
            <div style={s.modalBody}>
              {advanceConfirm.existingAllocations.length === 0 ? (
                <p style={{ margin: 0, lineHeight: 1.6 }}>
                  Bu tahsilatın tamamı zaten <b>avans</b> (dağıtılmamış) olarak duruyor. Faturaya
                  eşleşene kadar bu kuyrukta görünmeye devam eder.
                </p>
              ) : (
                <p style={{ margin: 0, lineHeight: 1.6 }}>
                  Bu tahsilatın <b>{advanceConfirm.existingAllocations.length} faturaya</b> bağlı
                  {' '}<b>{fmtMoney(advanceConfirm.existingAllocations.reduce((t, a) => t + a.allocatedAmount, 0))}</b>
                  {' '}eşleştirmesi <b>geri alınacak</b> ve tahsilatın tamamı
                  {' '}({fmtMoney(advanceConfirm.transaction.amount)}) avansa dönecek.
                  Bağlı faturaların açık tutarı yeniden açılır. Devam edilsin mi?
                </p>
              )}
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={() => setAdvanceConfirm(null)} disabled={busy}>Vazgeç</button>
              <button style={s.btnPrimary} onClick={leaveAsAdvance} disabled={busy}>
                {busy ? 'İşleniyor…' : 'Avans Olarak Bırak'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default UnallocatedCollectionsPage;
